import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import ParticlesAuth from "../../AuthenticationInner/ParticlesAuth";
import { Link, Navigate } from "react-router-dom";
import withRouter from "../../../Components/Common/withRouter";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { getBrandLogos } from "../../../helpers/brandPortal";
import {
  APIClient,
  getLoggedinSuperAdmin,
  getLoggedinUser,
  persistSuperAdminSession,
  saUrl,
} from "../../../helpers/api_helper";

const api = new APIClient();

/** Mensagem amigável conforme status/código da API (DOC 13 — sem error.code na UI). */
const resolveLoginError = (err: any, t: (key: string) => string): string => {
  if (err?.status === 429 || err?.code === "RATE_LIMIT_EXCEEDED") {
    // Copy específica de login (mais clara que o genérico da API).
    return t("sa.login.error.rateLimited");
  }
  if (err?.status === 401 || err?.code === "UNAUTHORIZED") {
    return err?.message || t("sa.login.error.invalidCredentials");
  }
  return err?.message || t("sa.login.error.generic");
};

/**
 * Login Super Admin — POST /super-admin/login (fora de /api/v1).
 * Sessão em `sa_authUser`, isolada do tenant.
 */
const SuperAdminLogin = (props: any) => {
  const { t, i18n } = useTranslation();
  const [passwordShow, setPasswordShow] = useState(false);
  const [loader, setLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorTone, setErrorTone] = useState<"danger" | "warning">("danger");
  // sessionStorage é por aba — detecta sessão tenant nesta mesma aba (não cross-tab).
  const [tenantSessionHere] = useState(() => {
    const tenant = getLoggedinUser();
    return !!(tenant?.access_token || tenant?.token);
  });

  // Sem prefill/hint DEV: painel SA público não deve vazar seed ([07] · [02]).
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("sa.login.validation.emailInvalid"))
        .required(t("sa.login.validation.emailRequired")),
      password: Yup.string().required(t("sa.login.validation.passwordRequired")),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      setErrorMessage(null);
      setErrorTone("danger");
      try {
        // URL absoluta: base Axios é /api/v1; SA vive em /super-admin.
        const envelope: any = await api.create(saUrl("/super-admin/login"), {
          email: values.email,
          password: values.password,
        });
        if (!envelope?.success || !envelope?.data?.access_token) {
          setErrorMessage(
            envelope?.error?.message || t("sa.login.error.generic")
          );
          setLoader(false);
          return;
        }
        persistSuperAdminSession(envelope.data);
        props.router.navigate("/super-admin/dashboard");
      } catch (err: any) {
        if (err?.status === 429 || err?.code === "RATE_LIMIT_EXCEEDED") {
          setErrorTone("warning");
        }
        setErrorMessage(resolveLoginError(err, t));
        setLoader(false);
      }
    },
  });

  useEffect(() => {
    validation.setFormikState((prev) => ({
      ...prev,
      validationSchema: Yup.object({
        email: Yup.string()
          .email(t("sa.login.validation.emailInvalid"))
          .required(t("sa.login.validation.emailRequired")),
        password: Yup.string().required(
          t("sa.login.validation.passwordRequired")
        ),
      }),
    }));
    // Yup reage à troca de idioma (DOC i18n).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18n.language]);

  document.title = t("sa.login.documentTitle");

  const existing = getLoggedinSuperAdmin();
  const existingToken = existing?.access_token || existing?.token;
  if (existingToken) {
    return <Navigate to="/super-admin/dashboard" replace />;
  }

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4">
                  <Link to="/super-admin/login" className="d-inline-block auth-logo">
                    <img src={getBrandLogos().dark} alt={getBrandLogos().alt} height="20" />
                  </Link>
                  <p className="mt-3 fs-15 fw-medium text-muted">{t("sa.login.brandSubtitle")}</p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 card-bg-fill">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">{t("sa.login.title")}</h5>
                      <p className="text-muted">{t("sa.login.subtitle")}</p>
                    </div>

                    {tenantSessionHere ? (
                      <Alert color="info" className="mt-3" role="status">
                        {t("sa.login.tenantSessionHint")}
                      </Alert>
                    ) : null}

                    {errorMessage ? (
                      <Alert color={errorTone} className="mt-3" role="alert">
                        {errorMessage}
                      </Alert>
                    ) : null}

                    <div className="p-2 mt-4">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="mb-3">
                          <Label htmlFor="sa-email" className="form-label">
                            {t("sa.login.email")}
                          </Label>
                          <Input
                            name="email"
                            id="sa-email"
                            className="form-control"
                            placeholder={t("sa.login.emailPlaceholder")}
                            type="email"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.email || ""}
                            invalid={
                              !!(
                                validation.touched.email &&
                                validation.errors.email
                              )
                            }
                          />
                          {validation.touched.email &&
                          validation.errors.email ? (
                            <FormFeedback type="invalid">
                              {String(validation.errors.email)}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label
                            className="form-label"
                            htmlFor="sa-password-input"
                          >
                            {t("sa.login.password")}
                          </Label>
                          <div className="position-relative auth-pass-inputgroup mb-3">
                            <Input
                              name="password"
                              id="sa-password-input"
                              value={validation.values.password || ""}
                              type={passwordShow ? "text" : "password"}
                              className="form-control pe-5"
                              placeholder={t("sa.login.passwordPlaceholder")}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                !!(
                                  validation.touched.password &&
                                  validation.errors.password
                                )
                              }
                            />
                            {validation.touched.password &&
                            validation.errors.password ? (
                              <FormFeedback type="invalid">
                                {String(validation.errors.password)}
                              </FormFeedback>
                            ) : null}
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              onClick={() => setPasswordShow(!passwordShow)}
                              aria-label={t("sa.login.togglePassword")}
                            >
                              <i className="ri-eye-fill align-middle"></i>
                            </button>
                          </div>
                        </div>

                        <div className="mt-4">
                          <Button
                            color="primary"
                            disabled={loader}
                            className="btn btn-primary w-100"
                            type="submit"
                          >
                            {loader && (
                              <Spinner size="sm" className="me-2">
                                …
                              </Spinner>
                            )}
                            {t("sa.login.submit")}
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(SuperAdminLogin);
